import { Controller } from "@hotwired/stimulus";

/**
 * Password Edit Form Controller
 * Validates password and password confirmation on the password reset edit form.
 * Password must be at least 8 characters and both fields must match.
 */

export default class extends Controller<HTMLFormElement> {
  static targets = [
    "password",
    "passwordConfirmation",
    "submit",
    "passwordError",
    "passwordConfirmationError",
  ];

  declare readonly passwordTarget: HTMLInputElement;
  declare readonly passwordConfirmationTarget: HTMLInputElement;
  declare readonly submitTarget: HTMLButtonElement;
  declare readonly passwordErrorTarget: HTMLElement;
  declare readonly passwordConfirmationErrorTarget: HTMLElement;
  declare readonly hasPasswordErrorTarget: boolean;
  declare readonly hasPasswordConfirmationErrorTarget: boolean;

  connect(): void {
    this.validateForm();
    this.element.addEventListener("input", this.handleFormInput);
  }

  disconnect(): void {
    this.element.removeEventListener("input", this.handleFormInput);
  }

  private handleFormInput = (): void => {
    this.validateForm();
  };

  validatePassword(): void {
    const input = this.passwordTarget;
    const isValid = input.value.length >= 8;

    this.toggleFieldValidation(input, isValid);

    if (this.hasPasswordErrorTarget) {
      if (!isValid && input.value.length > 0) {
        this.passwordErrorTarget.textContent =
          "Password must be at least 8 characters";
        this.passwordErrorTarget.classList.remove("hidden");
      } else {
        this.passwordErrorTarget.classList.add("hidden");
      }
    }

    // Also validate confirmation if it has a value
    if (this.passwordConfirmationTarget.value.length > 0) {
      this.validatePasswordMatch();
    }

    this.validateForm();
  }

  validatePasswordMatch(): void {
    const input = this.passwordConfirmationTarget;
    const isValid =
      input.value === this.passwordTarget.value && input.value.length >= 8;

    this.toggleFieldValidation(input, isValid);

    if (this.hasPasswordConfirmationErrorTarget) {
      if (!isValid && input.value.length > 0) {
        if (input.value !== this.passwordTarget.value) {
          this.passwordConfirmationErrorTarget.textContent =
            "Passwords don't match";
        } else {
          this.passwordConfirmationErrorTarget.textContent =
            "Password must be at least 8 characters";
        }
        this.passwordConfirmationErrorTarget.classList.remove("hidden");
      } else {
        this.passwordConfirmationErrorTarget.classList.add("hidden");
      }
    }

    this.validateForm();
  }

  private validateForm(): void {
    requestAnimationFrame(() => {
      const password = this.passwordTarget.value;
      const passwordConfirmation = this.passwordConfirmationTarget.value;

      const isPasswordValid = password.length >= 8;
      const isPasswordMatch = password === passwordConfirmation;

      const isFormValid = isPasswordValid && isPasswordMatch;

      this.submitTarget.disabled = !isFormValid;
    });
  }

  private toggleFieldValidation(
    input: HTMLInputElement,
    isValid: boolean
  ): void {
    if (input.value.length === 0) {
      input.classList.remove("input-success", "input-error");
      return;
    }

    if (isValid) {
      input.classList.add("input-success");
      input.classList.remove("input-error");
    } else {
      input.classList.add("input-error");
      input.classList.remove("input-success");
    }
  }
}
